import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import HeroCarousel from './components/HeroCarousel';
import VideoCard from '@/components/feature/VideoCard';
import {
  formatViews,
  getCategories,
  getFeaturedVideos,
  getHotVideos,
  getLatestVideos,
  getVideosByCategory,
} from '@/utils/video';

const PAGE_SIZE = 8;

export default function Home() {
  const categories = getCategories();
  const featured = getFeaturedVideos();
  const latest = useMemo(() => getLatestVideos(), []);
  const hot = useMemo(() => getHotVideos(6), []);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  const visibleVideos = latest.slice(0, visibleCount);
  const hasMore = visibleCount < latest.length;

  const stats = [
    { icon: 'ri-film-line', value: '30+', label: '精选视频' },
    { icon: 'ri-play-circle-line', value: '2,400万+', label: '累计播放' },
    { icon: 'ri-emotion-happy-line', value: '12.8万', label: '宅友聚集' },
  ];

  return (
    <div className="w-full">
      {featured.length > 0 && <HeroCarousel videos={featured} />}

      <section className="w-full px-4 md:px-6 py-10 md:py-14">
        <div className="flex items-end justify-between mb-6 md:mb-8">
          <div>
            <h2 className="text-xl md:text-2xl font-bold text-foreground-950">分类快捷入口</h2>
            <p className="mt-1.5 text-sm text-foreground-600">三大频道任你挑，点进去就是你的主场</p>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
          {categories.map((cat) => {
            const count = getVideosByCategory(cat.slug).length;
            return (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className="group relative overflow-hidden rounded-lg border border-background-200 bg-background-50 hover:border-accent-300 transition-colors cursor-pointer"
              >
                <div className="relative h-40 md:h-48 overflow-hidden bg-primary-900">
                  <img
                    src={cat.cover}
                    alt={`${cat.name}分类`}
                    title={`${cat.name} - 宅男视频`}
                    className="w-full h-full object-top object-cover opacity-90 transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-primary-950/90 via-primary-950/40 to-transparent"></div>
                  <span className="absolute top-3 left-3 flex items-center justify-center w-10 h-10 rounded-lg bg-accent-500 text-primary-950">
                    <i className={`${cat.icon} text-xl`}></i>
                  </span>
                  <div className="absolute bottom-3 left-4 right-4">
                    <h3 className="text-lg font-bold text-background-50">{cat.name}</h3>
                    <p className="text-xs text-primary-100 mt-0.5">{cat.short} · 共 {count} 个视频</p>
                  </div>
                </div>
                <p className="p-4 text-sm text-foreground-600 leading-relaxed line-clamp-2">{cat.description}</p>
              </Link>
            );
          })}
        </div>
      </section>

      <section className="w-full bg-background-50 border-y border-background-200">
        <div className="w-full px-4 md:px-6 py-10 md:py-14">
          <div className="flex items-center justify-between mb-6 md:mb-8">
            <div>
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950">最新更新</h2>
              <p className="mt-1.5 text-sm text-foreground-600">按上线时间排序，追新不迷路</p>
            </div>
            <span className="hidden sm:inline-flex items-center gap-1.5 text-xs text-foreground-500">
              <i className="ri-refresh-line"></i>
              共 {latest.length} 个视频
            </span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {visibleVideos.map((video) => (
              <VideoCard key={video.id} video={video} />
            ))}
          </div>

          <div className="mt-8 flex flex-col items-center gap-3">
            {hasMore ? (
              <button
                type="button"
                onClick={() => setVisibleCount((prev) => prev + PAGE_SIZE)}
                className="inline-flex items-center gap-2 h-11 px-8 rounded-md bg-primary-500 text-background-50 font-medium text-sm whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
              >
                <i className="ri-add-line"></i>
                加载更多
              </button>
            ) : (
              <p className="text-sm text-foreground-500">已经到底啦，去分类页发现更多内容吧</p>
            )}
            <span className="text-xs text-foreground-500">
              已显示 {visibleVideos.length} / {latest.length}
            </span>
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-6 py-10 md:py-14">
        <div className="flex items-center justify-between mb-6 md:mb-8">
          <div>
            <h2 className="text-xl md:text-2xl font-bold text-foreground-950">热门推荐</h2>
            <p className="mt-1.5 text-sm text-foreground-600">按播放量精选，宅友们都在看</p>
          </div>
          <Link to="/category/animation" className="hidden sm:inline-flex items-center gap-1 text-sm text-accent-600 hover:text-accent-700 transition-colors cursor-pointer">
            更多热门
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-5">
          {hot.map((video, index) => (
            <Link
              key={video.id}
              to={`/detail/${video.id}`}
              className="group flex gap-3 md:gap-4 rounded-lg bg-background-50 border border-background-200 p-3 hover:border-accent-300 transition-colors cursor-pointer"
            >
              <div className="relative w-32 sm:w-40 md:w-44 shrink-0 aspect-video overflow-hidden rounded-md bg-background-200">
                <img
                  src={video.cover}
                  alt={video.title}
                  title={`${video.title} - 宅男视频`}
                  className="w-full h-full object-top object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <span className="absolute top-1.5 left-1.5 flex items-center justify-center w-6 h-6 rounded bg-accent-500 text-primary-950 text-xs font-bold">
                  {index + 1}
                </span>
              </div>
              <div className="flex flex-col min-w-0 flex-1 py-0.5">
                <h3 className="text-sm font-semibold text-foreground-950 line-clamp-2 group-hover:text-accent-600 transition-colors">
                  {video.title}
                </h3>
                <p className="mt-1.5 text-xs text-foreground-600 line-clamp-2 hidden sm:block">{video.recommendReason}</p>
                <div className="mt-auto pt-2 flex items-center gap-3 text-xs text-foreground-500">
                  <span className="inline-flex items-center gap-1">
                    <i className="ri-play-fill"></i>
                    {formatViews(video.views)}
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <i className="ri-user-3-line"></i>
                    {video.author}
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <section className="w-full bg-primary-900">
        <div className="w-full px-4 md:px-6 py-12 md:py-16">
          <div className="max-w-3xl">
            <h2 className="text-xl md:text-2xl font-bold text-background-50">关于宅男视频</h2>
            <p className="mt-4 text-sm md:text-base text-primary-100 leading-relaxed">
              宅男视频是一个聚合动画、游戏与影视片段短视频的内容平台，专为喜爱二次元、游戏与电影剪辑的年轻用户打造。
              我们把碎片时间变成快乐时光：清晰的分类、快捷的筛选、即点即看的浏览体验，让每一段空闲都不无聊。
            </p>
            <div className="mt-8 flex flex-wrap gap-4 md:gap-8">
              {stats.map((item) => (
                <div key={item.label} className="flex items-center gap-3">
                  <span className="flex items-center justify-center w-11 h-11 rounded-lg bg-primary-800 text-accent-400">
                    <i className={`${item.icon} text-xl`}></i>
                  </span>
                  <div>
                    <p className="text-lg font-bold text-background-50">{item.value}</p>
                    <p className="text-xs text-primary-200">{item.label}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/about"
                className="inline-flex items-center gap-2 h-11 px-6 rounded-md bg-accent-500 text-primary-950 font-semibold text-sm whitespace-nowrap hover:bg-accent-400 transition-colors cursor-pointer"
              >
                了解我们
                <i className="ri-arrow-right-line"></i>
              </Link>
              <Link
                to="/search"
                className="inline-flex items-center gap-2 h-11 px-6 rounded-md border border-primary-300 text-background-50 font-medium text-sm whitespace-nowrap hover:bg-primary-800 transition-colors cursor-pointer"
              >
                <i className="ri-search-line"></i>
                搜索内容
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}